import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { FixtureChart } from '@sorare/core/src/components/player/FixtureChart';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { sortByDate } from '@sorare/core/src/lib/dates';
import {
  THRESHOLD_COLORS,
  findThreshold,
} from '@sorare/core/src/lib/footballThresholds';
import { glossary } from '@sorare/core/src/lib/glossary';

import FootballCardProperties from '@sorare/marketplace/src/components/card/FootballCardProperties';

import { ChartQuery, ChartQueryVariables } from './__generated__/index.graphql';

const Root = styled.div`
  background: linear-gradient(
    to top,
    var(--c-neutral-100) 50%,
    rgba(var(--c-rgb-neutral-100), 0.1)
  );
  backdrop-filter: blur(20px);
  outline: 2px solid rgba(var(--c-rgb-neutral-1000), 0.1);
  padding: var(--unit);
  border-radius: 20px;
  width: min-content;
`;

const CHART_QUERY = gql`
  query ChartQuery($slug: String!, $cardSlug: String!) {
    football {
      card(slug: $cardSlug) {
        slug
        ...FootballCardProperties_card
      }
    }
    anyPlayer(slug: $slug) {
      slug
      allPlayerGameScores(first: 7) {
        nodes {
          id
          score
          scoreStatus
          anyPlayerGameStats {
            id
            anyGame {
              id
              date
            }
          }
        }
      }
    }
  }
  ${FootballCardProperties.fragments.card}
` as TypedDocumentNode<ChartQuery, ChartQueryVariables>;

type Props = {
  slug: string;
};
export const Chart = ({ slug }: Props) => {
  const { data } = useQuery(CHART_QUERY, {
    variables: {
      slug,
      cardSlug: `${slug}-2024-limited-1`,
    },
  });
  const fixturesStats = sortByDate(
    [...(data?.anyPlayer.allPlayerGameScores.nodes || [])],
    gameScore => gameScore.anyPlayerGameStats.anyGame.date
  ).map(gameScore => ({
    key: gameScore.id,
    label: '',
    games: [
      {
        id: gameScore.anyPlayerGameStats.anyGame.id,
        playerScore: gameScore.score,
        startDate: gameScore.anyPlayerGameStats.anyGame.date,
        color: THRESHOLD_COLORS[findThreshold(gameScore.score)],
      },
    ],
  }));

  return (
    <Root>
      <FixtureChart
        yMaxValue={100}
        fixturesStats={fixturesStats || []}
        showBorder={false}
      />
      {data?.football.card && (
        <SBHorizontal>
          <FootballCardProperties
            card={data?.football.card}
            withTransferMalus={false}
          />
          <Button size="small" color="primary" asSpan>
            <FormattedMessage {...glossary.buy} />
          </Button>
        </SBHorizontal>
      )}
    </Root>
  );
};
